// src/shared/bug-reporter/hooks/useKeyboardShortcut.ts
// Cmd/Ctrl + Shift + B opens the reporter. Listens at window scope so it
// works even when no app element has focus, and ignores fired-from-input cases
// to avoid stealing the chord from text editors.

import { useEffect } from 'react'

interface Options {
  enabled: boolean
  onTrigger: () => void
}

export function useKeyboardShortcut({ enabled, onTrigger }: Options) {
  useEffect(() => {
    if (!enabled) return

    const handler = (e: KeyboardEvent) => {
      // Cmd on Mac, Ctrl elsewhere. Shift + B.
      const cmdOrCtrl = e.metaKey || e.ctrlKey
      if (!cmdOrCtrl || !e.shiftKey) return
      if (e.key.toLowerCase() !== 'b') return

      // If the user is typing in a content-editable region with Cmd+Shift+B
      // (rare — used in some text editors), let the host handle it. We only
      // intercept when focus is outside editable content.
      const target = e.target as HTMLElement | null
      if (target?.isContentEditable) return

      e.preventDefault()
      onTrigger()
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled, onTrigger])
}
